import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { ThemePalette } from '@angular/material/core';

@Component({
  selector: 'ts-radio-group',
  standalone: true,
  imports: [MatRadioModule, FormsModule],
  styleUrls: ['./ts-radio-group.component.scss'],
  template: `
    <mat-radio-group
      [ngModel]="value"
      (ngModelChange)="onValueChange($event)"
      [color]="color"
      [disabled]="isDisabled"
      [name]="name"
      [labelPosition]="labelPosition"
      [required]="required"
      (change)="change.emit($event)"
    >
      <ng-content />
    </mat-radio-group>
  `,
  host: { class: 'ts-radio-group' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: TsRadioGroupComponent,
      multi: true,
    },
  ],
})
export class TsRadioGroupComponent implements ControlValueAccessor {
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() color: ThemePalette = 'primary';
  @Input() disabled = false;
  @Input() name: string;
  @Input() labelPosition: 'before' | 'after' = 'after';
  @Input() required = false;

  @Output() change = new EventEmitter<MatRadioChange>();

  value: unknown;
  private formDisabled = false;

  get isDisabled(): boolean {
    return this.disabled || this.formDisabled;
  }

  writeValue(value: unknown): void {
    this.value = value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled = isDisabled;
    this.cdr.markForCheck();
  }

  onValueChange(value: unknown): void {
    this.value = value;
    this.onChange(value);
    this.onTouched();
  }

  private onChange: (value: unknown) => void = () => {};
  private onTouched: () => void = () => {};
}
